import React, { useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useAuth } from '../../contexts/AuthContext';
import { suggestionService } from '../../services/suggestionService';
import { isApprovedServiceAdmin } from '../../utils/accessControl';
import { Alert, Button, FormLabel, Input, Spinner } from '../common/UIComponents';
import { CheckCircleIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';

interface SuggestionFormData {
  originalTitle: string;
  originalDescription: string;
}

interface SuggestionFormProps {
  onSuggestionAdded?: (suggestionId: string) => void;
  onCancel?: () => void;
  compact?: boolean;
  initialFocusRef?: React.MutableRefObject<HTMLInputElement | null>;
}

const SuggestionForm: React.FC<SuggestionFormProps> = ({
  onSuggestionAdded,
  onCancel,
  compact = false,
  initialFocusRef,
}) => {
  const { currentUser, userProfile, claims } = useAuth();
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<SuggestionFormData>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const submitInFlightRef = useRef(false);

  const canCreateSuggestion = isApprovedServiceAdmin(userProfile, claims);

  const onSubmit = async (data: SuggestionFormData) => {
    if (submitInFlightRef.current) {
      return;
    }

    if (!currentUser) {
      setError('Musisz być zalogowany, aby dodać sugestię.');
      return;
    }

    if (!canCreateSuggestion) {
      setError('Tylko aktywny serviceAdmin może dodawać sugestie v2.');
      return;
    }

    submitInFlightRef.current = true;
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await suggestionService.submitSuggestionV2({
        originalTitle: data.originalTitle,
        originalDescription: data.originalDescription,
      });
      setSuccess('Sugestia została wysłana do opracowania.');
      reset();
      onSuggestionAdded?.(response.suggestionId);
    } catch (err: any) {
      console.error('Błąd dodawania sugestii:', err);
      const code = String(err?.code || '');
      if (code.includes('permission-denied')) {
        setError('Brak uprawnień do dodania sugestii.');
      } else if (code.includes('unauthenticated')) {
        setError('Sesja wygasła. Zaloguj się ponownie.');
      } else if (code.includes('invalid-argument')) {
        setError('Sprawdź długość tytułu i opisu sugestii.');
      } else {
        setError('Nie udało się dodać sugestii.');
      }
    } finally {
      submitInFlightRef.current = false;
      setLoading(false);
    }
  };

  if (!currentUser) {
    return <Alert variant="info">Musisz być zalogowany, aby dodać sugestię.</Alert>;
  }

  if (!canCreateSuggestion) {
    return (
      <Alert variant="info">
        Ten widok jest tylko do odczytu. Dodawanie sugestii v2 jest dostępne wyłącznie dla aktywnego
        serviceAdmina.
      </Alert>
    );
  }

  const originalTitleRegistration = register('originalTitle', {
    required: 'Tytuł jest wymagany',
  });

  const setOriginalTitleRef = (element: HTMLInputElement | null) => {
    originalTitleRegistration.ref(element);
    if (initialFocusRef) {
      initialFocusRef.current = element;
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className={compact ? 'space-y-3' : 'space-y-4'}>
      {error && (
        <Alert variant="error" icon={<ExclamationCircleIcon className="w-5 h-5" />}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert variant="success" icon={<CheckCircleIcon className="w-5 h-5" />}>
          {success}
        </Alert>
      )}

      <Input
        id="originalTitle"
        label="Oryginalny tytuł"
        placeholder="Krótki, zwięzły tytuł"
        required
        error={errors.originalTitle?.message}
        {...originalTitleRegistration}
        ref={setOriginalTitleRef}
      />

      <div>
        <FormLabel required>Oryginalny opis</FormLabel>
        <textarea
          id="originalDescription"
          rows={compact ? 4 : 6}
          className={`
            form-input mt-1 block w-full rounded-lg border border-primary-dark-300 bg-primary-dark-200
            text-primary-light-100 placeholder-primary-light-400 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-ratuj-300
            disabled:cursor-not-allowed disabled:opacity-50
            ${errors.originalDescription ? 'border-status-error-500 focus:ring-status-error-500' : ''}
          `}
          placeholder="Opisz szczegółowo problem, potrzebę lub pomysł..."
          {...register('originalDescription', { required: 'Opis jest wymagany' })}
        />
        {errors.originalDescription && (
          <p className="form-error">{errors.originalDescription.message}</p>
        )}
      </div>

      <div className="flex flex-wrap gap-2 pt-2">
        <Button
          type="submit"
          variant="primary"
          disabled={loading}
          className="py-2 px-4 text-sm font-medium"
        >
          {loading ? (
            <>
              <Spinner size="sm" color="white" className="mr-2" />
              Wysyłanie...
            </>
          ) : (
            'Wyślij sugestię'
          )}
        </Button>
        {onCancel && (
          <Button type="button" variant="neutral" onClick={onCancel} disabled={loading}>
            Anuluj
          </Button>
        )}
      </div>
    </form>
  );
};

export default SuggestionForm;
